import { supabase } from "@/lib/supabase";
import useAuthStore from "@/store/authStore";
import { isOnboardingComplete } from "@/utils/auth";
import { Loader2 } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";

// Module-level cache to prevent duplicate profile fetches across route changes
const profileFetchCache = {
  lastFetchedUserId: null,
  isFetching: false,
};

export function ProtectedRoute({ children, requireOnboarding = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { user, profile, initialize, initialized } = useAuthStore();

  // Routes that require onboarding if enabled
  const routesRequiringOnboarding = [
    "/chat",
    "/settings",
    "/dashboard",
    "/tools",
    "/assistants",
    "/prompts",
  ];

  // Check if the route requires onboarding check
  const shouldCheckOnboarding = () => {
    if (!requireOnboarding) return false;
    if (location.pathname === "/auth/onboarding") return false;

    return routesRequiringOnboarding.some(
      (route) =>
        location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
  };

  // Initialize auth state
  useEffect(() => {
    // Allow skipping auth for docs
    if (location.pathname.startsWith("/docs")) {
      setIsLoading(false);
      return;
    }

    // Check for OAuth flag
    const isFromOAuth =
      localStorage.getItem("auth_redirect_completed") ||
      document.referrer.includes("/auth/callback");

    const initAuth = async () => {
      if (initialized && !isFromOAuth) {
        // Even if initialized, ensure we have fresh profile data for onboarding check
        if (user && location.pathname !== "/auth/onboarding") {
          // Skip if already fetching or just fetched this user (prevents duplicates)
          if (
            profileFetchCache.isFetching ||
            profileFetchCache.lastFetchedUserId === user.id
          ) {
            setIsLoading(false);
            return;
          }

          try {
            profileFetchCache.isFetching = true;
            profileFetchCache.lastFetchedUserId = user.id;

            const { data: freshProfile, error: profileError } = await supabase
              .from("profiles")
              .select("*")
              .eq("id", user.id)
              .single();

            if (!profileError && freshProfile) {
              // Update the profile in auth store if there's a difference
              if (
                profile?.onboarding_completed !==
                freshProfile.onboarding_completed
              ) {
                useAuthStore.getState().setProfile(freshProfile);
              }
            }
          } catch (error) {
            console.error(
              "Error fetching fresh profile in ProtectedRoute:",
              error
            );
          } finally {
            profileFetchCache.isFetching = false;
          }
        }

        setIsLoading(false);
        return;
      }

      // Special handling for post-OAuth state
      if (isFromOAuth) {
        // Ensure we have the latest session data
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Error getting session after OAuth:", error);
          setIsLoading(false);
          return;
        }

        if (data?.session) {
          // Check if workspace data is available
          const { userId, currentWorkspaceId, workspaces } =
            useAuthStore.getState();

          if (
            userId &&
            (!currentWorkspaceId || !workspaces || workspaces.length === 0)
          ) {
            try {
              // Fetch workspaces directly
              const { data: workspacesData, error: wsError } = await supabase
                .from("workspaces")
                .select("*")
                .eq("user_id", userId);

              if (wsError) {
                console.error("Error fetching workspaces:", wsError);
              } else if (workspacesData && workspacesData.length > 0) {
                // Set workspaces in auth store
                useAuthStore.getState().setWorkspaces(workspacesData);

                // Set current workspace
                const personalWorkspace =
                  workspacesData.find((w) => w.is_personal) ||
                  workspacesData[0];
                useAuthStore
                  .getState()
                  .setCurrentWorkspaceId(personalWorkspace.id);
              } else {
                console.warn("No workspaces found for user in ProtectedRoute");
              }
            } catch (err) {
              console.error("Error loading workspace data:", err);
            }
          }

          // OAuth login successful - force a fresh init cycle
          try {
            setIsLoading(true);
            await initialize();

            // Verify key data after initialization
            const { userId, currentWorkspaceId, workspaces } =
              useAuthStore.getState();
          } catch (error) {
            console.error("Failed to initialize after OAuth:", error);
          } finally {
            // Clear OAuth flag regardless of outcome
            localStorage.removeItem("auth_redirect_completed");
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } else {
        // Normal auth initialization
        try {
          setIsLoading(true);
          await initialize();
        } catch (error) {
          console.error("Auth initialization error:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    initAuth();
  }, [location.pathname, initialize, initialized]);

  // Skip auth for docs routes
  if (location.pathname.startsWith("/docs")) {
    return children;
  }

  // Check if authenticated — redirect to landing page if not
  if (!user) {
    return (
      <Navigate
        to="/"
        state={{
          from: location,
        }}
        replace
      />
    );
  }

  // Get onboarding status
  const userHasCompletedOnboarding = isOnboardingComplete(profile);

  // If we don't have a profile yet but have a user, we need to wait
  if (user && !profile && !isLoading) {
    // Trigger a profile fetch
    useAuthStore
      .getState()
      .initialize()
      .then(() => {})
      .catch((error) => {
        console.error("ProtectedRoute: Error fetching profile:", error);
      });

    // Show loading while profile is being fetched
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin mb-4 text-primary" />
        <p className="text-muted-foreground">Loading profile...</p>
      </div>
    );
  }

  // Redirect to onboarding if needed
  if (shouldCheckOnboarding() && !userHasCompletedOnboarding) {
    return (
      <Navigate to="/auth/onboarding" state={{ from: location }} replace />
    );
  }

  // Redirect away from onboarding page if completed
  if (location.pathname === "/auth/onboarding" && userHasCompletedOnboarding) {
    return <Navigate to="/chat" replace />;
  }

  // Successfully authenticated and onboarding requirements met
  return children;
}

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requireOnboarding: PropTypes.bool,
};

export default ProtectedRoute;
